import { Alert, Button, Col, Form, FormGroup, Input, Label, Row } from 'reactstrap';
import { AmsPermissionClaimType, authService } from '../../services/Auth';
import {
    ProfessionAddOnTrack,
    ProfessionDto,
    ProfessionLevel,
    ProfessionsConcentrationDto,
    Program as ProgramModel,
    ProgramAddress,
    ProgramExtension,
} from '../../common/Types';
import React, { useEffect, useState } from 'react';

import moment from 'moment';
import { professionService } from '../../services/ProfessionAdministration';
import { programService } from '../../services/ProgramService';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { Countries, DegreeType, DegreeTypes, ExtensionStatus, ExtensionType, ProgramStatus, States } from '../../common/Data';
import { parseUserInfo } from '../../common/Utils';

interface ProgramProps {
    program: ProgramModel;
    professionConcentrations: ProfessionsConcentrationDto[];
    professionAddOnTracks: ProfessionAddOnTrack[];
    onSaved: () => void;
}

const Program = (props: ProgramProps) => {
    const [professionConcentrations, setProfessionConcentrations] = useState<ProfessionsConcentrationDto[]>();
    const [professionAddOnTracks, setProfessionAddOnTracks] = useState<ProfessionAddOnTrack[]>();
    const [editing, setEditing] = useState(false);
    const [program, setProgram] = useState<ProgramModel>({ ...props.program });
    const [professions, setProfessions] = useState<ProfessionDto[]>();
    const [levels, setLevels] = useState<ProfessionLevel[]>();
    const [filteredLevels, setFilteredLevels] = useState<ProfessionLevel[]>();
    const [isInternational, setIsInternational] = useState<boolean>(false);
    const { handleSubmit, control, watch, reset, setValue } = useForm();

    const [programExtensions, setProgramExtensions] = useState<ProgramExtension[]>();
    const [extensionType, setExtensionType] = useState<ExtensionType>();

    const watchProfessionId = watch('professionId');
    const watchProgram = watch();
    const country = watch('programAddress.country');

    useEffect(() => {
        if (country && country !== 'United States of America') {
            setIsInternational(true);
        } else {
            setIsInternational(false);
        }
    }, [country, setValue]);

    useEffect(() => {
        setProgram({
            ...props.program,
            dateFirstAccredited: props.program.dateFirstAccredited
                ? moment.utc(props.program.dateFirstAccredited).format('YYYY-MM-DD')
                : '',
            nextFullEvaluation: props.program.nextFullEvaluation
                ? moment.utc(props.program.nextFullEvaluation).format('YYYY-MM-DD')
                : '',
            prDue: props.program.prDue ? moment.utc(props.program.prDue).format('YYYY-MM-DD') : '',
            programAddress: props.program.programAddress
                ? props.program.programAddress
                : ({
                      country: 'United States of America',
                  } as ProgramAddress),
        });
        setProfessionAddOnTracks(props.professionAddOnTracks);
        setProfessionConcentrations(props.professionConcentrations);
    }, [setProgram, props.program, props.professionAddOnTracks, props.professionConcentrations]);

    useEffect(() => {
        professionService
            .getProfessions()
            .then((response) => {
                setProfessions(response);
                reset(program);
            })
            .catch(() => {
                toast.error('Failed to load professions.');
            });

        professionService
            .getAllLevels()
            .then((response) => {
                setLevels(response);
                if (program && program.professionId) {
                    setFilteredLevels(response.filter((l) => l.professionId === program.professionId));
                }
                reset(program);
            })
            .catch(() => {
                toast.error('Failed to load levels.');
            });

        programService
            .getProgramExtensions(props.program.programId)
            .then((results) => {
                setProgramExtensions([...results]);
                setExtensionType(results?.find((r) => r.isDeleted === false)?.extensionTypeId);
            })
            .catch(() => {
                toast.error('Error loading program details');
            });
    }, [setProfessions, setLevels, setProgramExtensions, reset, program, props.program.programId]);

    useEffect(() => {
        if (watchProfessionId && watchProfessionId > 0) {
            setFilteredLevels(levels?.filter((c) => c.professionId === +watchProfessionId));
        } else {
            setFilteredLevels([]);
        }
    }, [watchProfessionId, levels]);

    const onSubmit = (program: ProgramModel) => {
        // Custom validation for profession level requirement
        const selectedProfessionHasLevels = levels?.some((l) => l.professionId === program.professionId);
        if (selectedProfessionHasLevels && (!program.professionLevelId || program.professionLevelId <= 0)) {
            toast.error('A profession level is required when the selected profession has levels available.');
            return;
        }
        const toastId = toast.info('Saving program...');
        programService
            .saveProgram(program)
            .then((result) => {
                toast.update(toastId, {
                    render: 'Program saved successfully',
                    type: 'success',
                });

                setProgram({
                    ...result,
                    dateFirstAccredited: result.dateFirstAccredited
                        ? moment.utc(result.dateFirstAccredited).format('YYYY-MM-DD')
                        : '',
                    nextFullEvaluation: result.nextFullEvaluation
                        ? moment.utc(result.nextFullEvaluation).format('YYYY-MM-DD')
                        : '',
                    prDue: result.prDue ? moment.utc(result.prDue).format('YYYY-MM-DD') : '',
                });
                reset(result);
                setEditing(false);
                props.onSaved();
            })
            .catch(() => {
                toast.update(toastId, {
                    render: 'Error saving program',
                    type: 'error',
                });
            });
    };

    const selectedProfession = professions?.find((p) => p.professionId === +(watchProgram.professionId || -1));

    const programTitle = `${selectedProfession?.title || ''}`;

    // Determine if concentrations are required but missing
    const hasConcentrationsEnabled = professionConcentrations && professionConcentrations.length > 0;
    const validConcentrationExtensions = programExtensions?.filter(pe =>
        !pe.isDeleted &&
        pe.concentrationId &&
        pe.activeStatus !== ExtensionStatus.Removed
    ) || [];
    const hasConcentrations = validConcentrationExtensions.length > 0;
    const concentrationsMissing = hasConcentrationsEnabled && programExtensions !== undefined && !hasConcentrations;

    const editedByInfo = parseUserInfo(program.editedByString);

    return (
        <div className={'flex-grow-1'}>
            <Form noValidate className={'h-100'} onSubmit={handleSubmit((values) => onSubmit(new ProgramModel(values)))}>
                <Controller
                    name="programId"
                    control={control}
                    render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                />
                <Controller
                    name="institutionId"
                    control={control}
                    render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                />
                <Controller
                    name="createdBy"
                    control={control}
                    render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                />
                <Controller
                    name="dateCreated"
                    control={control}
                    render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                />
                <Controller
                    name="lastEdited"
                    control={control}
                    render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                />
                <Controller
                    name="editedBy"
                    control={control}
                    render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                />
                <Controller
                    name="active"
                    control={control}
                    render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                />
                <Controller
                    name="onProbation"
                    control={control}
                    render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                />
                <Controller
                    name="probationReason"
                    control={control}
                    render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                />
                <Controller
                    name="probationStartDate"
                    control={control}
                    render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                />
                <Controller
                    name="title"
                    control={control}
                    render={({ field }) => <input type="hidden" {...field} value={programTitle ?? ''} />}
                />
                <Controller
                    name="programAddressId"
                    control={control}
                    render={({ field }) => <input type={'hidden'} {...field} value={field.value ?? ''} />}
                />

                <div className={'d-flex flex-column justify-content-between h-100'}>
                    <div>
                        {program.onProbation === true && (
                            <Row>
                                <Col>
                                    <Alert
                                        color={'warning'}
                                        transition={{ timeout: 0 }}
                                    >{`This program is on administrative probation.`}</Alert>
                                </Col>
                            </Row>
                        )}

                        {concentrationsMissing && (
                            <Row>
                                <Col>
                                    <Alert
                                        color={'danger'}
                                        transition={{ timeout: 0 }}
                                    >
                                        This program requires at least one concentration. Please add concentrations using the Extensions section below before saving changes.
                                    </Alert>
                                </Col>
                            </Row>
                        )}

                        <Row>
                            <Col sm={12} lg={4}>
                                <FormGroup className={'row'}>
                                    <Col xs={12}>
                                        <Label>Profession</Label>
                                    </Col>
                                    <Col xs={12}>
                                        {!editing && professions && (
                                            <>
                                                <span>
                                                    {
                                                        professions.find((p) => p.professionId === program.professionId)
                                                            ?.title
                                                    }
                                                </span>
                                                <Controller
                                                    name="professionId"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input type={'hidden'} {...field} value={field.value ?? ''} />
                                                    )}
                                                />
                                            </>
                                        )}
                                        {editing && (
                                            <Controller
                                                name="professionId"
                                                control={control}
                                                rules={{ required: true }}
                                                render={({ field }) => (
                                                    <select
                                                        className={'select form-control'}
                                                        {...field}
                                                        value={field.value ?? ''}
                                                    >
                                                        <option value={''}>Select a Profession</option>
                                                        {professions &&
                                                            professions.map((profession) => (
                                                                <option
                                                                    key={profession.professionId}
                                                                    value={profession.professionId}
                                                                >
                                                                    {profession.title}
                                                                </option>
                                                            ))}
                                                    </select>
                                                )}
                                            />
                                        )}
                                    </Col>
                                </FormGroup>
                            </Col>
                            <Col sm={12} lg={4}>
                                {filteredLevels && filteredLevels.length > 0 && (
                                    <FormGroup className={'row'}>
                                        <Col xs={12}>
                                            <Label>
                                                Level <span className="text-danger">*</span>
                                            </Label>
                                        </Col>
                                        <Col xs={12}>
                                            {!editing && levels && program.professionLevelId && (
                                                <>
                                                    <span>
                                                        {levels.find((l) => l.id === program.professionLevelId)?.title}
                                                    </span>
                                                    <Controller
                                                        name="professionLevelId"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <input
                                                                type={'hidden'}
                                                                {...field}
                                                                value={field.value ?? ''}
                                                            />
                                                        )}
                                                    />
                                                </>
                                            )}
                                            {editing && (
                                                <Controller
                                                    name="professionLevelId"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <select
                                                            className={'select form-control'}
                                                            {...field}
                                                            value={field.value ?? ''}
                                                        >
                                                            <option value={''}>Select a Level (Required)</option>
                                                            {filteredLevels.map((level) => (
                                                                <option key={level.id} value={level.id}>
                                                                    {level.title}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    )}
                                                />
                                            )}
                                        </Col>
                                    </FormGroup>
                                )}
                                {(!filteredLevels || filteredLevels?.length === 0) && (
                                    <Controller
                                        name="professionLevelId"
                                        control={control}
                                        render={({ field }) => (
                                            <input type="hidden" {...field} value={field.value ?? ''} />
                                        )}
                                    />
                                )}
                                {(() => {
                                    // Check if this is a deactivated/legacy program
                                    const isDeactivatedProgram = program.activeStatus === ProgramStatus['Not Applicable'] || program.active === false;

                                    // For deactivated programs, show legacy concentration if available
                                    // Check both concentrationId and concentration object (backend may send either)
                                    const legacyConcentrationId = program.concentrationId || program.concentration?.concentrationId;
                                    if (isDeactivatedProgram && legacyConcentrationId) {
                                        // First check if concentration object is already loaded
                                        const legacyConcentration = program.concentration ||
                                            professionConcentrations?.find((c) => c.concentrationId === legacyConcentrationId);
                                        if (legacyConcentration) {
                                            return (
                                                <FormGroup className={'row'}>
                                                    <Col xs={12}>
                                                        <Label>Concentration (Legacy)</Label>
                                                    </Col>
                                                    <Col xs={12}>
                                                        <div>
                                                            <span>{legacyConcentration.title}</span>
                                                        </div>
                                                    </Col>
                                                </FormGroup>
                                            );
                                        }
                                    }

                                    // Filter extensions that are not deleted and not removed
                                    const validExtensions = programExtensions?.filter((pe) =>
                                        !pe.isDeleted && pe.activeStatus !== ExtensionStatus.Removed
                                    ) || [];

                                    if (validExtensions.length === 0) return null;

                                    // Determine what types of extensions exist based on actual data, not just extensionTypeId
                                    const concentrationExtensions = validExtensions.filter((pe) =>
                                        pe.concentrationId && professionConcentrations?.find(c => c.concentrationId === pe.concentrationId)
                                    );
                                    const addOnExtensions = validExtensions.filter((pe) =>
                                        pe.addOnTrackId && professionAddOnTracks?.find(a => a.addOnTrackId === pe.addOnTrackId)
                                    );

                                    const hasConcentrations = concentrationExtensions.length > 0;
                                    const hasAddOnTracks = addOnExtensions.length > 0;

                                    // Determine label - simplified since professions only have one type
                                    let label = '';
                                    if (hasConcentrations && hasAddOnTracks) {
                                        label = 'Extensions';
                                    } else if (hasConcentrations) {
                                        label = 'Concentration(s)';
                                    } else if (hasAddOnTracks) {
                                        label = 'Add On Track(s)';
                                    }

                                    return (
                                        <FormGroup className={'row'}>
                                            <Col xs={12}>
                                                <Label>{label}</Label>
                                            </Col>
                                            <Col xs={12}>
                                                {concentrationExtensions.map((pe) => (
                                                    <div key={pe.id}>
                                                        <span>
                                                            {
                                                                professionConcentrations?.find(
                                                                    (c) => c.concentrationId === pe.concentrationId,
                                                                )?.title
                                                            }
                                                        </span>
                                                    </div>
                                                ))}
                                                {addOnExtensions.map((pe) => (
                                                    <div key={pe.id}>
                                                        <span>
                                                            {
                                                                professionAddOnTracks?.find(
                                                                    (paot) => paot.addOnTrackId === pe.addOnTrackId,
                                                                )?.title
                                                            }
                                                        </span>
                                                    </div>
                                                ))}
                                            </Col>
                                        </FormGroup>
                                    );
                                })()}
                            </Col>
                            <Col sm={12} lg={4}>
                                <FormGroup className={'row'}>
                                    <Col xs={12}>
                                        {!editing && (
                                            <>
                                                <Label>Visible:</Label>
                                                <span className={'ml-2'}>{program.visible ? 'Yes' : 'No'}</span>
                                            </>
                                        )}
                                        {editing && (
                                            <div className={'custom-control custom-checkbox'}>
                                                <Controller
                                                    name="visible"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="checkbox"
                                                            id={`visible`}
                                                            {...field}
                                                            checked={field.value ?? false}
                                                            className={'custom-control-input'}
                                                        />
                                                    )}
                                                />
                                                <Label for={`visible`} className={'custom-control-label'}>
                                                    Visible?
                                                </Label>
                                            </div>
                                        )}
                                    </Col>
                                </FormGroup>
                                <FormGroup className={'row'}>
                                    <Col xs={12}>
                                        {!editing && (
                                            <>
                                                <Label>Distance:</Label>
                                                <span className={'ml-2'}>{program.distance ? 'Yes' : 'No'}</span>
                                            </>
                                        )}
                                        {editing && (
                                            <div className={'custom-control custom-checkbox'}>
                                                <Controller
                                                    name="distance"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="checkbox"
                                                            id={`distance`}
                                                            {...field}
                                                            checked={field.value ?? false}
                                                            className={'custom-control-input'}
                                                        />
                                                    )}
                                                />
                                                <Label for={`distance`} className={'custom-control-label'}>
                                                    Distance?
                                                </Label>
                                            </div>
                                        )}
                                    </Col>
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col sm={12} lg={4}>
                                <FormGroup className={'row'}>
                                    <Col xs={12}>
                                        <Label>Program Name</Label>
                                    </Col>
                                    {!editing && (
                                        <Col xs={12}>
                                            <span>{program.programName}</span>
                                        </Col>
                                    )}
                                    {editing && (
                                        <Col xs={12}>
                                            <div>
                                                <Controller
                                                    name="programName"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            id="programName"
                                                            placeholder="Program Name"
                                                            {...field}
                                                            value={field.value ?? ''}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </Col>
                                    )}
                                </FormGroup>
                            </Col>
                            <Col sm={12} lg={4}>
                                <FormGroup className={'row'}>
                                    <Col xs={12}>
                                        <Label>Degree</Label>
                                    </Col>
                                    <Col xs={12}>
                                        {/* Show degreeTypeId if set */}
                                        {!editing && program.degreeTypeId === DegreeType.Diploma && (
                                            <span className={'d-block'}>Diploma</span>
                                        )}
                                        {!editing && program.degreeTypeId === DegreeType.Certificate && (
                                            <span className={'d-block'}>Certificate</span>
                                        )}
                                        {!editing && program.degreeTypeId === DegreeType.Associate && (
                                            <span className={'d-block'}>Associate</span>
                                        )}
                                        {!editing && program.degreeTypeId === DegreeType.Baccalaureate && (
                                            <span className={'d-block'}>Baccalaureate</span>
                                        )}
                                        {!editing && program.degreeTypeId === DegreeType.Masters && (
                                            <span className={'d-block'}>Masters</span>
                                        )}
                                        {/* For deactivated programs without degreeTypeId, show legacy degree booleans */}
                                        {!editing && !program.degreeTypeId && (
                                            <>
                                                {program.degreeDiploma && <span className={'d-block'}>Diploma (Legacy)</span>}
                                                {program.degreeCertificate && <span className={'d-block'}>Certificate (Legacy)</span>}
                                                {program.degreeAssociate && <span className={'d-block'}>Associate (Legacy)</span>}
                                                {program.degreeBaccalaureate && <span className={'d-block'}>Baccalaureate (Legacy)</span>}
                                                {program.degreeMasters && <span className={'d-block'}>Masters (Legacy)</span>}
                                            </>
                                        )}
                                    </Col>
                                    {editing && (
                                        <>
                                            <Controller
                                                name="degreeTypeId"
                                                control={control}
                                                rules={{ required: true }}
                                                render={({ field }) => (
                                                    <select
                                                        className={'select form-control'}
                                                        {...field}
                                                        value={field.value ?? ''}
                                                    >
                                                        <option value={''}>Select a Degree</option>
                                                        <option value={DegreeType.Diploma}>Diploma</option>
                                                        <option value={DegreeType.Certificate}>Certificate</option>
                                                        <option value={DegreeType.Associate}>Associate</option>
                                                        <option value={DegreeType.Baccalaureate}>Baccalaureate</option>
                                                        <option value={DegreeType.Masters}>Masters</option>
                                                    </select>
                                                )}
                                            />
                                        </>
                                    )}
                                </FormGroup>
                            </Col>
                            <Col sm={12} lg={4}>
                                <FormGroup className={'row'}>
                                    <Col xs={12}>
                                        {!editing && (
                                            <>
                                                <Label>Military Only:</Label>
                                                <span className={'ml-2'}>
                                                    {program.militaryPersonnelOnly ? 'Yes' : 'No'}
                                                </span>
                                            </>
                                        )}
                                        {editing && (
                                            <div className={'custom-control custom-checkbox'}>
                                                <Controller
                                                    name="militaryPersonnelOnly"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="checkbox"
                                                            id={`militaryPersonnelOnly`}
                                                            {...field}
                                                            checked={field.value ?? false}
                                                            className={'custom-control-input'}
                                                        />
                                                    )}
                                                />
                                                <Label for={`militaryPersonnelOnly`} className={'custom-control-label'}>
                                                    Military Only?
                                                </Label>
                                            </div>
                                        )}
                                    </Col>
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col sm={12} lg={4}>
                                <FormGroup className={'row'}>
                                    <Col xs={12}>
                                        <Label>Address</Label>
                                    </Col>
                                    {!editing && (
                                        <>
                                            <Col xs={12}>
                                                <div>{program.programAddress?.address || ''}</div>
                                                {program.programAddress?.address2 && (
                                                    <div>{program.programAddress?.address2}</div>
                                                )}
                                                {program.programAddress?.address3 && (
                                                    <div>{program.programAddress?.address3}</div>
                                                )}
                                                {program.programAddress?.address4 && (
                                                    <div>{program.programAddress?.address4}</div>
                                                )}
                                                <div>{`${program.programAddress?.city || ''}, ${
                                                    program.programAddress?.state || ''
                                                } ${program.programAddress?.zip || ''}`}</div>
                                                <div>{program.programAddress?.country || ''}</div>
                                            </Col>
                                        </>
                                    )}
                                    {editing && (
                                        <Col xs={12}>
                                            <div>
                                                <Controller
                                                    name="programAddress.address"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            id="programAddress.address"
                                                            placeholder="Address"
                                                            {...field}
                                                            value={field.value ?? ''}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="mt-1">
                                                <Controller
                                                    name="programAddress.address2"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            id="programAddress.address2"
                                                            placeholder="Address 2"
                                                            {...field}
                                                            value={field.value ?? ''}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="mt-1">
                                                <Controller
                                                    name="programAddress.address3"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            id="programAddress.address3"
                                                            placeholder="Address 3"
                                                            {...field}
                                                            value={field.value ?? ''}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="mt-1">
                                                <Controller
                                                    name="programAddress.address4"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="text"
                                                            id="programAddress.address4"
                                                            placeholder="Address 4"
                                                            {...field}
                                                            value={field.value ?? ''}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="mt-1">
                                                <div className="d-inline-block w-50">
                                                    <Controller
                                                        name="programAddress.city"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                type="text"
                                                                id="programAddress.city"
                                                                placeholder="City"
                                                                {...field}
                                                                value={field.value ?? ''}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                                <div className="d-inline-block w-50 p-1">
                                                    {!isInternational && (
                                                        <Controller
                                                            name="programAddress.state"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Input
                                                                    type={'select'}
                                                                    id="programAddress.state"
                                                                    {...field}
                                                                    value={field.value ?? ''}
                                                                >
                                                                    <option
                                                                        value={undefined}
                                                                    >{`Select State...`}</option>
                                                                    {States &&
                                                                        States.map((s) => (
                                                                            <option
                                                                                key={s.abbreviation}
                                                                                value={s.abbreviation}
                                                                            >
                                                                                {s.abbreviation}
                                                                            </option>
                                                                        ))}
                                                                </Input>
                                                            )}
                                                        />
                                                    )}
                                                    {isInternational && (
                                                        <Controller
                                                            name="programAddress.state"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Input
                                                                    type="text"
                                                                    id="programAddress.state"
                                                                    placeholder="State/Province"
                                                                    {...field}
                                                                    value={field.value ?? ''}
                                                                />
                                                            )}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            <div className="mt-1">
                                                <div className="d-inline-block w-25">
                                                    <Controller
                                                        name="programAddress.zip"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                type="text"
                                                                id="programAddress.zip"
                                                                placeholder={isInternational ? `Postal Code` : `Zip`}
                                                                {...field}
                                                                value={field.value ?? ''}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                                <div className="d-inline-block w-75 p-1">
                                                    <Controller
                                                        name="programAddress.country"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={'select'}
                                                                id="programAddress.country"
                                                                {...field}
                                                                value={field.value ?? ''}
                                                            >
                                                                <option value={undefined}></option>
                                                                {Countries.map((c) => (
                                                                    <option key={c} value={c}>
                                                                        {c}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        )}
                                                    />
                                                </div>
                                                <Controller
                                                    name="programAddress.id"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input type={'hidden'} {...field} value={field.value ?? ''} />
                                                    )}
                                                />
                                                <Controller
                                                    name="programAddress.dateCreated"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input type={'hidden'} {...field} value={field.value ?? ''} />
                                                    )}
                                                />
                                                <Controller
                                                    name="programAddress.createdByString"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input type={'hidden'} {...field} value={field.value ?? ''} />
                                                    )}
                                                />
                                            </div>
                                        </Col>
                                    )}
                                </FormGroup>
                            </Col>
                            <Col sm={12} lg={4}>
                                <FormGroup className={'row'}>
                                    <Col xs={12}>
                                        <Label>Program Website</Label>
                                    </Col>
                                    <Col xs={12}>
                                        {!editing && program.website && (
                                            <a
                                                href={`${program?.website?.indexOf('http://') > -1 ? '' : 'http://'}${
                                                    program.website
                                                }`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {program.website}&nbsp;
                                                <i className={'remixicon-links-line'}></i>
                                            </a>
                                        )}
                                        {!editing && !program.website && 'N/A'}
                                        {editing && (
                                            <Controller
                                                name="website"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type={'text'} {...field} value={field.value ?? ''} />
                                                )}
                                            />
                                        )}
                                    </Col>
                                </FormGroup>
                            </Col>
                            <Col sm={12} lg={4}>
                                <FormGroup className={'row'}>
                                    <Col xs={12}>
                                        <Label>Outcomes Url</Label>
                                    </Col>
                                    <Col xs={12}>
                                        {!editing && program.outcomesUrl && (
                                            <a
                                                href={`${
                                                    program?.outcomesUrl?.indexOf('http://') > -1 ? '' : 'http://'
                                                }${program.outcomesUrl}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {program.outcomesUrl}&nbsp;
                                                <i className={'remixicon-links-line'}></i>
                                            </a>
                                        )}
                                        {!editing && !program.outcomesUrl && 'N/A'}
                                        {editing && (
                                            <Controller
                                                name="outcomesUrl"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type={'text'} {...field} value={field.value ?? ''} />
                                                )}
                                            />
                                        )}
                                    </Col>
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col sm={12} lg={4}>
                                <FormGroup className={'row'}>
                                    <Col xs={12}>
                                        <Label>Date First Accredited</Label>
                                    </Col>
                                    <Col xs={12}>
                                        {!editing && (
                                            <span>
                                                {program.dateFirstAccredited
                                                    ? moment(program.dateFirstAccredited).format('MM/DD/YYYY')
                                                    : ''}
                                            </span>
                                        )}
                                        {editing && (
                                            <Controller
                                                name="dateFirstAccredited"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input {...field} type={'date'} value={field.value ?? ''} />
                                                )}
                                            />
                                        )}
                                    </Col>
                                </FormGroup>
                            </Col>
                            <Col sm={12} lg={4}>
                                <FormGroup className={'row'}>
                                    <Col xs={12}>
                                        <Label>Next Full Evaluation</Label>
                                    </Col>
                                    <Col xs={12}>
                                        {!editing && (
                                            <span>
                                                {program.nextFullEvaluation
                                                    ? moment(program.nextFullEvaluation).format('MM/DD/YYYY')
                                                    : ''}
                                            </span>
                                        )}
                                        {editing && (
                                            <Controller
                                                name="nextFullEvaluation"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input {...field} type={'date'} value={field.value ?? ''} />
                                                )}
                                            />
                                        )}
                                    </Col>
                                </FormGroup>
                            </Col>
                            <Col sm={12} lg={4}>
                                <FormGroup className={'row'}>
                                    <Col xs={12}>
                                        <Label>PR Due</Label>
                                    </Col>
                                    <Col xs={12}>
                                        {!editing && (
                                            <span>
                                                {program.prDue ? moment(program.prDue).format('MM/DD/YYYY') : ''}
                                            </span>
                                        )}
                                        {editing && (
                                            <Controller
                                                name="prDue"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input {...field} type={'date'} value={field.value ?? ''} />
                                                )}
                                            />
                                        )}
                                    </Col>
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col sm={12} lg={12}>
                                <FormGroup className={'row'}>
                                    <Col xs={12}>
                                        <Label>Credentialing Code</Label>
                                    </Col>
                                    <Col xs={12}>
                                        {!editing && <span>{program.credentialingCode}</span>}
                                        {editing && (
                                            <Controller
                                                name="credentialingCode"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type={'text'} {...field} value={field.value ?? ''} />
                                                )}
                                            />
                                        )}
                                    </Col>
                                </FormGroup>
                            </Col>
                        </Row>
                    </div>
                    <div className="force-left-align" style={{ display: 'flex', justifyContent: 'flex-start', textAlign: 'left', alignItems: 'flex-start' }}>
                        {!editing && authService.hasPermission(AmsPermissionClaimType, 'systemadmin') && (
                            <Button type={'button'} color={'primary'} onClick={() => setEditing(!editing)}>
                                Edit
                            </Button>
                        )}
                        {editing && (
                            <>
                                <Button color={'primary'} type={'submit'} disabled={concentrationsMissing}>
                                    Save
                                </Button>

                                <Button color="light" className={'ml-2'} onClick={() => setEditing(!editing)}>
                                    Cancel
                                </Button>
                            </>
                        )}
                    </div>
                    {(editedByInfo || program.lastEdited) && (
                        <div className={'mt-3'}>
                            <span>{`Last edited`}</span>
                            {editedByInfo && <span>{` by ${editedByInfo.name}`}</span>}
                            {program.lastEdited && (
                                <span>{` on ${moment
                                    .utc(program.lastEdited)
                                    .local()
                                    .format('MM/DD/YYYY [at] hh:mm A')}`}</span>
                            )}
                        </div>
                    )}
                </div>
            </Form>
        </div>
    );
};

export default Program;
